import React, { useState, useMemo, useEffect } from 'react';
import { GlobalButton } from '../GlobalButton';
import { GlobalTextarea } from '../GlobalTextarea';
import { GlobalDropdown } from '../GlobalDropdown';
import GlobalTabs from '../GlobalTabs';
import SmallServiceCard from '../SmallServiceCard';
import { TestCaseCard } from './data/TestCaseCard';
import styles from './TestsTab.module.css';
import { useLocale } from '../../context/DynamicLocaleProvider';
import { useToast } from '../../context/ToastProvider';
import { useTheme } from '../../context/ThemeProvider';
import { FilterBar } from '../FilterBar';
import { TestCase } from '../../types';
import { createTestCase } from '../../services/domainEntities';
import GlobalEmptyState from '../GlobalEmptyState';
import GlobalSidebar from '../GlobalSidebar';
import { collection, getDocs, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useUser } from '../../context/UserContext';
import DoubleConfirmationModal from '../DoubleConfirmationModal';
import { URSAutoComplete } from '../URSAutoComplete';
import { FSAutoComplete } from '../FSAutoComplete';
import { RiskAutoComplete } from '../RiskAutoComplete';
import { SOPAutoComplete } from '../SOPAutoComplete';
import TransformEntitiesSidebar from '../TransformEntitiesSidebar';
import EntityGenerationSidebar from '../EntityGenerationSidebar';
import GenerateEntitySidebar from '../GenerateEntitySidebar';
import AddDocumentSidebar from '../AddDocumentSidebar';
import { exportToCSV } from '../../utils/exportToCSV';

interface TestsTabProps {
  projectId: string;
  onExport?: () => void;
  onNavigateToEntity?: (entityId: string, entityType: 'URS' | 'FS' | 'TEST' | 'RISK' | 'SOP') => void;
}

export const TestsTab: React.FC<TestsTabProps> = ({ projectId, onExport, onNavigateToEntity }) => {
  const { t } = useLocale();
  const showToast = useToast();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { user } = useUser();
  
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [selectedTest, setSelectedTest] = useState<TestCase | null>(null);
  const [showTestEditor, setShowTestEditor] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [testToDelete, setTestToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showTransformEntitiesSidebar, setShowTransformEntitiesSidebar] = useState(false);
  const [showEntityGenerationSidebar, setShowEntityGenerationSidebar] = useState(false);
  const [showGenerateEntitySidebar, setShowGenerateEntitySidebar] = useState(false);
  const [selectedEntitiesForGeneration, setSelectedEntitiesForGeneration] = useState<Array<{
    id: string;
    type: 'URS' | 'FS' | 'TEST' | 'RISK' | 'SOP';
    title: string;
  }>>([]);
  const [showAddDocumentSidebar, setShowAddDocumentSidebar] = useState(false);

  useEffect(() => {
    const loadTests = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const col = collection(db, 'projects', projectId, 'tests');
        const snapshot = await getDocs(col);
        const loaded: TestCase[] = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            ...data,
            id: data.id || `TEST-${doc.id.substring(0, 8)}`,
            _firestoreId: doc.id
          } as TestCase & { _firestoreId: string };
        });
        setTestCases(loaded);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error loading Tests:', err);
        }
        showToast(t('project_tab_test_load_failed') || 'Failed to load Tests', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadTests();
  }, [projectId, showToast, t]);

  const handleSaveTest = async (test: TestCase) => {
    try {
      const firestoreId = (test as any)?._firestoreId;
      
      if (firestoreId) {
        // Update existing
        const docRef = doc(db, 'projects', projectId, 'tests', firestoreId);
        const { _firestoreId, ...dataToSave } = test as any;
        await updateDoc(docRef, {
          ...dataToSave,
          updatedAt: serverTimestamp()
        } as any);
      } else {
        // Create new
        await createTestCase(projectId, test);
      }
      
      // Reload tests from Firestore
      const col = collection(db, 'projects', projectId, 'tests');
      const snapshot = await getDocs(col);
      const loaded: TestCase[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          id: data.id || `TEST-${doc.id.substring(0, 8)}`,
          _firestoreId: doc.id
        } as TestCase & { _firestoreId: string };
      });
      setTestCases(loaded);
      
      setShowTestEditor(false);
      setSelectedTest(null);
      showToast(t('project_tab_test_saved') || 'Test saved', 'success');
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(err);
      }
      showToast(t('project_tab_test_save_failed') || 'Failed to save test', 'error');
    }
  };

  const handleAddTest = () => {
    const newTest: TestCase = {
      id: `TEST-${String(testCases.length + 1).padStart(3, '0')}`,
      type: 'TEST',
      title: '',
      level: 'OQ',
      objective: '',
      preconditions: [],
      steps: [],
      acceptance_criteria: '',
      status: 'Draft',
      links: { urs_ids: [], fs_ids: [], risk_ids: [], sop_ids: [] },
      meta: { owner: '', tags: [] },
      createdAt: new Date(),
      updatedAt: new Date()
    };
    setSelectedTest(newTest);
    setShowTestEditor(true);
  };

  const handleAiGenerate = () => {
    // Open the Generate Entity sidebar
    setShowGenerateEntitySidebar(true);
  };

  const extractTESTNumber = (id: string): number => {
    const match = id.match(/(\d+)$/);
    return match ? parseInt(match[1], 10) : 0;
  };

  const handleDeleteTest = (testId: string) => {
    setTestToDelete(testId);
    setShowDeleteModal(true);
  };

  const confirmDeleteTest = async () => {
    if (!testToDelete) return;
    setIsDeleting(true);
    try {
      const test = testCases.find(t => t.id === testToDelete);
      if (test && (test as any)._firestoreId) {
        const docRef = doc(db, 'projects', projectId, 'tests', (test as any)._firestoreId);
        await deleteDoc(docRef);
      }
      setTestCases(testCases.filter(t => t.id !== testToDelete));
      showToast(t('project_tab_test_deleted') || 'Test deleted', 'success');
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(err);
      }
      showToast(t('project_tab_test_delete_failed') || 'Failed to delete test', 'error');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setTestToDelete(null);
    }
  };

  const handleEntityClick = (entityId: string, entityType: 'URS' | 'FS') => {
    onNavigateToEntity?.(entityId, entityType);
  };

  const handleAiGenerateFromTest = () => {
    // Open sidebar with ALL Tests selected
    if (testCases.length === 0) {
      showToast(t('project_tab_no_tests_to_generate') || 'No tests available. Please create a test first.', 'error');
      return;
    }
    const allTests = testCases.map(test => ({
      id: test.id,
      type: 'TEST' as const,
      title: test.title
    }));
    setSelectedEntitiesForGeneration(allTests);
    setShowEntityGenerationSidebar(true);
  };

  const handleUploadTestClick = () => {
    setShowAddDocumentSidebar(true);
  };

  // Filter and sort test cases based on search and status
  const filteredTestCases = useMemo(() => {
    const filtered = testCases.filter(test => {
      const matchesSearch = !searchValue || 
        test.id.toLowerCase().includes(searchValue.toLowerCase()) ||
        test.title.toLowerCase().includes(searchValue.toLowerCase()) ||
        test.objective.toLowerCase().includes(searchValue.toLowerCase());
      const matchesStatus = !statusFilter || test.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
    
    return filtered.sort((a, b) => {
      const numA = extractTESTNumber(a.id);
      const numB = extractTESTNumber(b.id);
      return numA - numB;
    });
  }, [testCases, searchValue, statusFilter]);

  const tabs = [
    {
      label: t('project_tab_test_cases') || 'Test Cases',
      queryValue: 'test-cases',
      content: (
        <div className={styles.testsSection}>
          {/* FilterBar */}
          <FilterBar
            filters={[
              {
                id: 'status',
                label: t('project_tab_status') || 'Status',
                options: [
                  { value: '', label: t('project_tab_all') || 'All' },
                  { value: 'Draft', label: t('project_tab_status_draft') || 'Draft' },
                  { value: 'Approved', label: t('project_tab_status_approved') || 'Approved' },
                  { value: 'Executed', label: t('project_tab_status_passed') || 'Executed' },
                  { value: 'Deviation', label: t('project_tab_status_failed') || 'Deviation' },
                  { value: 'Retired', label: t('project_tab_status_rejected') || 'Retired' }
                ],
                value: statusFilter,
                onChange: (value) => setStatusFilter(value)
              }
            ]}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchPlaceholder={t('project_tab_search_tests') || 'Search test cases...'}
            showInviteButton={true}
            onAddUser={handleAddTest}
            addButtonText={t('project_tab_add_test_case') || 'Add Test Case'}
          />

          {loading ? null : filteredTestCases.length === 0 ? (
            <GlobalEmptyState
              buttonText={t('project_tab_add_test_case') || 'Add Test Case'}
              buttonIcon={isDarkMode ? "/assets/icons/plus.circle.svg" : "/assets/icons/plus.circle.black.svg"}
              onButtonClick={handleAddTest}
            />
          ) : (
            <div className={styles.testsList}>
              {filteredTestCases.map((test) => (
                <div key={test.id} className={styles.testItem}>
                  <TestCaseCard
                    testCase={test}
                    projectId={projectId}
                    onEdit={(test) => {
                      setSelectedTest(test);
                      setShowTestEditor(true);
                    }}
                    onDelete={handleDeleteTest}
                    onRun={(test) => {
                      showToast(t('project_tab_running_test') || 'Running test...', 'info');
                    }}
                    onViewTraceability={(test) => {
                      showToast(t('project_tab_viewing_traceability') || 'Viewing traceability', 'info');
                    }}
                    onEntityClick={handleEntityClick}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )
    },
    {
      label: t('project_tab_options') || 'Options',
      queryValue: 'options',
      content: (
        <div className={styles.optionsGrid}>

          {/* Add test case */}
          <SmallServiceCard
            id="add-test"
            title={t('project_tab_add_test_case') || 'Add Test Case'}
            description={t('project_tab_add_test_case_desc') || 'Create a new test case'}
            fileTypeIcon=""
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/plus.circle.svg" : "/assets/icons/plus.circle.black.svg"}
            buttonText={t('project_tab_add') || 'Add'}
            buttonOnClick={handleAddTest}
          />

          {/* Import test plan */}
          <SmallServiceCard
            id="import-test-plan"
            title={t('project_tab_read_file_with_ai') || 'Datei mit KI einlesen'}
            description={t('project_tab_read_file_with_ai_tests_desc') || 'Importieren Sie Dateien um Testfälle automatisch auslesen und erstellen zu lassen.'}
            fileTypeIcon={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/icloud.and.arrow.up.svg" : "/assets/icons/icloud.and.arrow.up.black.svg"}
            buttonText={t('project_tab_generate') || 'Generieren'}
            buttonOnClick={handleUploadTestClick}
            disabled={!user}
          />

          {/* Generate from FS */}
          <SmallServiceCard
            id="generate-from-fs"
            title={t('project_tab_generate_from_fs') || 'Testfälle mit KI generieren'}
            description={t('project_tab_generate_from_fs_desc') || 'Nutzen sie KI um Testfälle aus den Funktionsspezifikationen automatisch zu generieren'}
            fileTypeIcon={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.right.circle.svg" :"/assets/icons/arrow.right.circle.black.svg"}
            buttonText={t('project_tab_generate') || 'Generate'}
            buttonOnClick={handleAiGenerate}
          />

          {/* Generate with AI from ALL Tests */}
          <SmallServiceCard
            id="ai-generate-from-all-tests"
            title={t('project_tab_ai_generate_from_all_tests') || 'Mit KI aus Test generieren'}
            description={t('project_tab_ai_generate_from_all_tests_desc') || 'Generieren Sie verbundene Entitäten (URS, Tests, Risiken, SOPs) mit KI wie aus den FS\'s'}
            fileTypeIcon={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.right.circle.svg" :"/assets/icons/arrow.right.circle.black.svg"}
            buttonText={t('project_tab_generate') || 'Generieren'}
            buttonOnClick={handleAiGenerateFromTest}
          />

          {/* Transform Entities */}
          <SmallServiceCard
            id="transform-entities-tests"
            title={t('project_tab_transform_tests') || 'Transform Test Case'}
            description={t('project_tab_transform_tests_desc') || 'Transform test cases to replace content, translate, or rephrase.'}
            fileTypeIcon={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.right.circle.svg" :"/assets/icons/arrow.right.circle.black.svg"}
            buttonText={t('project_tab_transform') || 'Transform'}
            buttonOnClick={() => setShowTransformEntitiesSidebar(true)}
          />

          {/* Export PDF */}
          <SmallServiceCard
            id="export-pdf-tests"
            title={t('project_tab_tests_export_pdf') || 'Als PDF exportieren'}
            description={t('project_tab_tests_export_pdf_desc') || 'Exportieren Sie alle Testfälle gesammelt als ein PDF Dokument.'}
            fileTypeIcon="/assets/graphics/pdf-icon.svg"
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.down.circle.white.svg" : "/assets/icons/arrow.down.circle.black.svg"}
            buttonText={t('project_tab_export') || 'Exportieren'}
            buttonOnClick={onExport || (() => showToast(t('project_tab_exporting') || 'Exporting...', 'info'))}
            disabled
          />

          {/* Export CSV */}
          <SmallServiceCard
            id="export-csv-tests"
            title={t('project_tab_tests_export_csv') || 'Als CSV exportieren'}
            description={t('project_tab_tests_export_csv_desc') || 'Exportieren Sie alle Testfälle gesammelt als ein CSV Dokument.'}
            fileTypeIcon={"/assets/graphics/csv_icon.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.down.circle.white.svg" : "/assets/icons/arrow.down.circle.black.svg"}
            buttonText={t('project_tab_export') || 'Exportieren'}
            buttonOnClick={() => {
              try {
                if (testCases.length === 0) {
                  showToast(t('project_tab_no_tests_to_export') || 'No test cases to export', 'warning');
                  return;
                }
                exportToCSV(testCases, `Tests_Export_${projectId}_${new Date().toISOString().split('T')[0]}`);
                showToast(t('project_tab_export_success') || 'Export successful', 'success');
              } catch (error) {
                console.error('Error exporting CSV:', error);
                showToast(t('project_tab_export_failed') || 'Export failed', 'error');
              }
            }}
          />
        </div>
      )
    }
  ];

  return (
    <div className={styles.testsTab}>
      <div className={styles.tabHeader}>
        <h2 className={styles.tabTitle}>
          {t('project_tab_tests_title') || 'Tests'}
        </h2>
      </div>

      <GlobalTabs tabs={tabs} queryParam="tab" defaultTabValue="test-cases" />

      {/* Entity Generation Sidebar */}
      <EntityGenerationSidebar
        isOpen={showEntityGenerationSidebar}
        onClose={() => {
          setShowEntityGenerationSidebar(false);
          setSelectedEntitiesForGeneration([]);
        }}
        projectId={projectId}
        selectedEntities={selectedEntitiesForGeneration}
        onSuccess={() => {
          // Reload tests to show any new entities
          const loadTests = async () => {
            try {
              const col = collection(db, 'projects', projectId, 'tests');
              const snapshot = await getDocs(col);
              const loaded: TestCase[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  ...data,
                  id: data.id || `TEST-${doc.id.substring(0, 8)}`,
                  _firestoreId: doc.id
                } as TestCase & { _firestoreId: string };
              });
              setTestCases(loaded);
            } catch (err) {
              if (process.env.NODE_ENV !== 'production') {
                console.error('Error reloading tests:', err);
              }
            }
          };
          loadTests();
        }}
      />

      {/* Generate Entity Sidebar */}
      <GenerateEntitySidebar
        isOpen={showGenerateEntitySidebar}
        onClose={() => {
          setShowGenerateEntitySidebar(false);
        }}
        projectId={projectId}
        entityType="TEST"
        onSuccess={() => {
          // Reload tests to show generated entity
          const loadTests = async () => {
            try {
              const col = collection(db, 'projects', projectId, 'tests');
              const snapshot = await getDocs(col);
              const loaded: TestCase[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  ...data,
                  id: data.id || `TEST-${doc.id.substring(0, 8)}`,
                  _firestoreId: doc.id
                } as TestCase & { _firestoreId: string };
              });
              setTestCases(loaded);
            } catch (err) {
              if (process.env.NODE_ENV !== 'production') {
                console.error('Error reloading tests:', err);
              }
            }
          };
          loadTests();
        }}
      />

      {/* Transform Entities Sidebar */}
      <TransformEntitiesSidebar
        isOpen={showTransformEntitiesSidebar}
        onClose={() => {
          setShowTransformEntitiesSidebar(false);
        }}
        projectId={projectId}
        onSuccess={() => {
          // Reload tests to show transformed entities
          const loadTests = async () => {
            try {
              const col = collection(db, 'projects', projectId, 'tests');
              const snapshot = await getDocs(col);
              const loaded: TestCase[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  ...data,
                  id: data.id || `TEST-${doc.id.substring(0, 8)}`,
                  _firestoreId: doc.id
                } as TestCase & { _firestoreId: string };
              });
              setTestCases(loaded);
            } catch (err) {
              if (process.env.NODE_ENV !== 'production') {
                console.error('Error reloading tests:', err);
              }
            }
          };
          loadTests();
        }}
      />

      <DoubleConfirmationModal
        isOpen={showDeleteModal}
        title={t('project_tab_delete_test_title')?.replace('{id}', testToDelete || '') || `Delete Test ${testToDelete}?`}
        paragraph={t('project_tab_delete_test_paragraph') || 'Are you sure you want to delete this test? This action cannot be undone.'}
        primaryLabel={t('global_delete') || 'Delete'}
        primaryIcon="/assets/icons/trash.red.svg"
        secondaryLabel={t('global_cancel') || 'Cancel'}
        secondaryIcon="/assets/icons/xmark.svg"
        onPrimary={confirmDeleteTest}
        onSecondary={() => {
          setShowDeleteModal(false);
          setTestToDelete(null);
        }}
        primaryDestructive
        loading={isDeleting}
      />

      {showTestEditor && selectedTest && (
        <TestEditor
          projectId={projectId}
          test={selectedTest}
          onSave={async (test) => {
            await handleSaveTest(test);
          }}
          onCancel={() => {
            setShowTestEditor(false);
            setSelectedTest(null);
          }}
          isNew={!testCases.some(t => t.id === selectedTest.id)}
        />
      )}

      {/* Add Document Sidebar */}
      <AddDocumentSidebar
        isOpen={showAddDocumentSidebar}
        onClose={() => setShowAddDocumentSidebar(false)}
        projectId={projectId}
        defaultEntityTypes={['TEST']}
        onSuccess={() => {
          // Reload test cases after document upload
          const loadTests = async () => {
            try {
              const col = collection(db, 'projects', projectId, 'tests');
              const snapshot = await getDocs(col);
              const loaded: TestCase[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  ...data,
                  id: data.id || `TEST-${doc.id.substring(0, 8)}`,
                  _firestoreId: doc.id
                } as TestCase & { _firestoreId: string };
              });
              setTestCases(loaded);
            } catch (err) {
              if (process.env.NODE_ENV !== 'production') {
                console.error('Error reloading tests:', err);
              }
            }
          };
          loadTests();
        }}
      />
    </div>
  );
};

interface TestEditorProps {
  projectId: string;
  test: TestCase;
  onSave: (test: TestCase) => Promise<void>;
  onCancel: () => void;
  isNew: boolean;
}

const TestEditor: React.FC<TestEditorProps> = ({ projectId, test, onSave, onCancel, isNew }) => {
  const { t } = useLocale();
  const [editedTest, setEditedTest] = useState<TestCase>(test);

  const parseList = (value: string) => value.split('\n').map(s => s.trim()).filter(Boolean);

  return (
    <GlobalSidebar isOpen={true} onClose={onCancel}>
      <div>
        <div className={styles.editorHeader}>
          <h3>
            {isNew
              ? (t('project_tab_test_add') || 'Add Test')
              : (t('project_tab_test_edit') || 'Edit Test')}
            <span className={styles.testId}> {editedTest.id}</span>
          </h3>
        </div>
        <div className={styles.editorContent}>
          <div className={styles.modalTabs}>
            <GlobalTabs
              tabs={[
                {
                  label: t('project_tab_test_details') || 'Details',
                  content: (
                    <div className={styles.modalTabSection}>
                      <GlobalTextarea
                        label={t('project_tab_title') || 'Title'}
                        value={editedTest.title}
                        onChange={(e) => setEditedTest({ ...editedTest, title: e.target.value })}
                        rows={2}
                      />
                      <GlobalTextarea
                        label={t('project_tab_objective') || 'Objective'}
                        value={editedTest.objective}
                        onChange={(e) => setEditedTest({ ...editedTest, objective: e.target.value })}
                        rows={3}
                      />
                      <GlobalTextarea
                        label={t('project_tab_preconditions') || 'Preconditions'}
                        value={editedTest.preconditions.join('\n')}
                        onChange={(e) => setEditedTest({ ...editedTest, preconditions: parseList(e.target.value) })}
                        rows={3}
                      />
                    </div>
                  )
                },
                {
                  label: t('project_tab_test_steps') || 'Test Steps',
                  content: (
                    <div className={styles.modalTabSection}>
                      <GlobalTextarea
                        label={t('project_tab_test_steps_hint') || 'Test Steps (one per line: Action | Expected)'}
                        value={editedTest.steps.map(s => `${s.action} | ${s.expected_result}`).join('\n')}
                        onChange={(e) => {
                          const steps = parseList(e.target.value).map((line, idx) => {
                            const [action, expected_result] = line.split('|').map(s => s.trim());
                            return { step_no: idx + 1, action: action || '', expected_result: expected_result || '' };
                          });
                          setEditedTest({ ...editedTest, steps });
                        }}
                        rows={6}
                      />
                      <GlobalTextarea
                        label={t('project_tab_acceptance_criteria') || 'Acceptance Criteria'}
                        value={editedTest.acceptance_criteria}
                        onChange={(e) => setEditedTest({ ...editedTest, acceptance_criteria: e.target.value })}
                        rows={3}
                      />
                    </div>
                  )
                },
                {
                  label: t('project_tab_test_classification') || 'Classification',
                  content: (
                    <div className={styles.modalTabSection}>
                      <GlobalDropdown
                        label={t('project_tab_level') || 'Level'}
                        value={editedTest.level}
                        onChange={(value) => setEditedTest({ ...editedTest, level: value as any })}
                        options={[
                          { value: 'IQ', label: 'IQ' },
                          { value: 'OQ', label: 'OQ' },
                          { value: 'PQ', label: 'PQ' },
                          { value: 'Unit', label: 'Unit' },
                          { value: 'Integration', label: 'Integration' },
                          { value: 'UAT', label: 'UAT' }
                        ]}
                      />
                      <GlobalDropdown
                        label={t('project_tab_status') || 'Status'}
                        value={editedTest.status}
                        onChange={(value) => setEditedTest({ ...editedTest, status: value as any })}
                        options={[
                          { value: 'Draft', label: t('project_tab_status_draft') || 'Draft' },
                          { value: 'Approved', label: t('project_tab_status_approved') || 'Approved' },
                          { value: 'Executed', label: t('project_tab_status_passed') || 'Executed' },
                          { value: 'Deviation', label: t('project_tab_status_failed') || 'Deviation' },
                          { value: 'Retired', label: t('project_tab_status_rejected') || 'Retired' }
                        ]}
                      />
                    </div>
                  )
                },
                {
                  label: t('project_tab_test_links') || 'Connections',
                  content: (
                    <div className={styles.modalTabSection}>
                      <URSAutoComplete
                        projectId={projectId}
                        label={t('project_tab_linked_urs') || 'Linked URS'}
                        placeholder={t('project_tab_search_requirements') || 'Search URS...'}
                        selectedIds={editedTest.links.urs_ids}
                        currentEntityId={editedTest.id}
                        currentEntityType="TEST"
                        onChange={(ids) =>
                          setEditedTest({
                            ...editedTest,
                            links: { ...editedTest.links, urs_ids: ids }
                          })
                        }
                      />
                      <FSAutoComplete
                        projectId={projectId}
                        label={t('project_tab_linked_fs') || 'Linked FS'}
                        placeholder={t('project_tab_search_specs') || 'Search FS...'}
                        selectedIds={editedTest.links.fs_ids}
                        currentEntityId={editedTest.id}
                        currentEntityType="TEST"
                        onChange={(ids) =>
                          setEditedTest({
                            ...editedTest,
                            links: { ...editedTest.links, fs_ids: ids }
                          })
                        }
                      />
                      <RiskAutoComplete
                        projectId={projectId}
                        label={t('project_tab_linked_risks') || 'Linked RISK'}
                        placeholder={t('project_tab_search_risks') || 'Search Risks...'}
                        selectedIds={editedTest.links.risk_ids}
                        currentEntityId={editedTest.id}
                        currentEntityType="TEST"
                        onChange={(ids) =>
                          setEditedTest({
                            ...editedTest,
                            links: { ...editedTest.links, risk_ids: ids }
                          })
                        }
                      />
                      <SOPAutoComplete
                        projectId={projectId}
                        label={t('project_tab_linked_sops') || 'Linked SOP'}
                        placeholder={t('project_tab_search_sops') || 'Search SOPs...'}
                        selectedIds={editedTest.links.sop_ids}
                        currentEntityId={editedTest.id}
                        currentEntityType="TEST"
                        onChange={(ids) =>
                          setEditedTest({
                            ...editedTest,
                            links: { ...editedTest.links, sop_ids: ids }
                          })
                        }
                      />
                    </div>
                  )
                }
              ]}
            />
          </div>
        </div>
        <div className={styles.editorActions}>
          <GlobalButton variant="secondary" onClick={onCancel}>
            {t('project_tab_cancel') || 'Cancel'}
          </GlobalButton>
          <GlobalButton onClick={async () => await onSave(editedTest)}>
            {t('project_tab_save') || 'Save'}
          </GlobalButton>
        </div>
      </div>
    </GlobalSidebar>
  );
};

